const cookiePrefix = 'flowscale_hub_';

interface CookieOptions {
  expires?: number | Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

export const cookies = {
  // Generic cookie methods
  set: (name: string, value: string, options: CookieOptions = {}) => {
    const defaults: CookieOptions = {
      path: '/',
      expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days default
      secure: false, // Set to false for HTTP
      sameSite: 'lax',
    };

    const config = { ...defaults, ...options };

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (config.expires) {
      const exp = typeof config.expires === 'number' ? new Date(config.expires) : config.expires;
      cookieString += `; expires=${exp.toUTCString()}`;
    }

    if (config.path) cookieString += `; path=${config.path}`;
    if (config.domain) cookieString += `; domain=${config.domain}`;
    if (config.secure) cookieString += '; secure';
    if (config.sameSite) cookieString += `; samesite=${config.sameSite}`;

    // Properly set the cookie
    document.cookie = cookieString;
  },

  get: (name: string): string | null => {
    const nameEQ = encodeURIComponent(name) + '=';
    const cookie = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(nameEQ));

    return cookie ? decodeURIComponent(cookie.substring(nameEQ.length)) : null;
  },

  remove: (name: string, options: Omit<CookieOptions, 'expires'> = {}) => {
    cookies.set(name, '', { ...options, expires: new Date(0) });
  },

  // ComfyOrg API Key helpers
  getComfyOrgAPIKey: (): string | null => {
    const raw = cookies.get('api_key_comfy_org');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (err) {
      console.error('Error parsing ComfyOrg API key from cookie:', err);
      return null;
    }
  },

  setComfyOrgAPIKey: (key: string) => {
    cookies.set('api_key_comfy_org', JSON.stringify(key), {
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year
      secure: false, // Set to false for HTTP
      sameSite: 'lax',
    });
  },

  clearComfyOrgAPIKey: () => {
    cookies.remove('api_key_comfy_org');
  },

  // Remove everything with our prefix or the ComfyOrg key
  clearAllAppCookies: () => {
    document.cookie
      .split(';')
      .map((c) => c.trim())
      .forEach((cookie) => {
        const eq = cookie.indexOf('=');
        const name = eq > -1 ? cookie.slice(0, eq) : cookie;
        const decodedName = decodeURIComponent(name);
        if (decodedName.startsWith(cookiePrefix) || decodedName === 'api_key_comfy_org') {
          cookies.remove(decodedName);
        }
      });
  },
};
